import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Order, ReturnRequestSummary, ShippingAddress } from "@aurelia/types";
import { apiFetch } from "../../lib/api-client";

interface OrderListResponse {
  items: Order[];
  total: number;
  page: number;
  pageSize: number;
}

export function useMyOrders(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["orders", "mine", page, pageSize],
    queryFn: () => apiFetch<OrderListResponse>(`/orders/mine?page=${page}&pageSize=${pageSize}`),
    placeholderData: (previous) => previous
  });
}

export function useOrder(id: string | undefined, pollPayment = false) {
  return useQuery({
    queryKey: ["order", id],
    queryFn: () => apiFetch<{ order: Order }>(`/orders/${id}`).then((r) => r.order),
    enabled: !!id,
    refetchInterval: pollPayment
      ? (query) => (query.state.data?.paymentStatus === "PENDING" || query.state.data?.paymentStatus === "INITIATED" ? 2000 : false)
      : false
  });
}

export interface PlaceOrderPayload {
  shippingAddress: ShippingAddress;
  contactPhone: string;
  discountCode?: string;
  coinsToUse?: number;
  paymentMethod: "ADVANCE_COD" | "SAFEPAY";
}

export interface PlaceOrderResponse {
  order: Order;
  checkoutUrl?: string;
  paymentError?: string;
}

export function usePlaceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlaceOrderPayload) =>
      apiFetch<PlaceOrderResponse>("/orders", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
    }
  });
}

export function useInitiateSafepay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      apiFetch<{ checkoutUrl: string }>(`/orders/${orderId}/payments/safepay`, { method: "POST" }),
    onSuccess: (_result, orderId) => queryClient.invalidateQueries({ queryKey: ["order", orderId] })
  });
}

export interface DiscountValidation {
  code: string;
  discountAmount: number;
}

export function useValidateDiscount() {
  return useMutation({
    mutationFn: ({ code, subtotal }: { code: string; subtotal: number }) =>
      apiFetch<DiscountValidation>("/discounts/validate", {
        method: "POST",
        body: JSON.stringify({ code, subtotal })
      })
  });
}

export function useTrackOrder() {
  return useMutation({
    mutationFn: ({ orderNumber, phone }: { orderNumber: string; phone: string }) =>
      apiFetch<{ order: Order }>("/orders/track", {
        method: "POST",
        body: JSON.stringify({ orderNumber, phone })
      }).then((r) => r.order)
  });
}

export function useRequestReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, orderItemId, reason }: { orderId: string; orderItemId: string; reason: string }) =>
      apiFetch<{ returnRequest: ReturnRequestSummary }>(`/orders/${orderId}/returns`, {
        method: "POST",
        body: JSON.stringify({ orderItemId, reason })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order"] });
    }
  });
}
